import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global context-free helper to open the inquiry modal from any CTA.
const openInquiryModal = () => {
    window.dispatchEvent(new CustomEvent("gc:open-inquiry"));
};

const useReveal = () => {
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
};

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#FBFBF9]/80 border-b border-[#05291C]/10 py-4"
                    : "bg-transparent py-6"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="logo-link"
                    className="flex items-baseline gap-2 group"
                >
                    <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#05291C] font-medium">
                        Gold
                    </span>
                    <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-[#4A5A53] mt-1">
                        Consulting
                    </span>
                </a>
                <nav className="hidden md:flex items-center gap-10 text-[12px] uppercase tracking-[0.22em] text-[#05291C]">
                    <a href="#what" data-testid="nav-what" className="link-underline font-medium">Practice</a>
                    <a href="#case" data-testid="nav-case" className="link-underline font-medium">Case Study</a>
                    <a href="#process" data-testid="nav-process" className="link-underline font-medium">Process</a>
                </nav>
                <button
                    type="button"
                    onClick={openInquiryModal}
                    data-testid="header-contact-button"
                    className="group inline-flex items-center gap-3 border border-[#05291C] text-[#05291C] hover:bg-[#05291C] hover:text-[#FBFBF9] transition-colors duration-500 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-medium cursor-pointer"
                >
                    Contact
                    <span className="block w-4 h-px bg-current transition-all duration-500 group-hover:w-6" />
                </button>
            </div>
        </header>
    );
};

const Hero = () => {
    return (
        <section id="top" data-testid="hero-section" className="relative pt-40 md:pt-48 pb-24 md:pb-32 overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-7 lg:col-span-7 reveal">
                        <div className="flex items-center gap-4 mb-10">
                            <span className="block w-10 h-px bg-[#B5965B]" />
                            <span className="text-[11px] uppercase tracking-[0.32em] text-[#4A5A53] font-medium">
                                Est. Private M&A Advisory
                            </span>
                        </div>
                        <h1
                            data-testid="hero-headline"
                            className="font-serif font-normal text-[#05291C] tracking-tight leading-[1.0] text-[16vw] sm:text-[12vw] md:text-[8vw] lg:text-[7.5rem] xl:text-[8.5rem]"
                        >
                            Sell with
                            <br />
                            <span className="italic font-normal">discretion.</span>
                        </h1>
                        <p className="mt-10 max-w-xl font-sans text-[#4A5A53] text-base md:text-lg leading-relaxed font-light">
                            Gold Consulting matches founders and owner-operators
                            with the precise investment banker built for their
                            transaction. Quiet introductions. Aligned
                            incentives. Outcomes worth signing for.
                        </p>
                        <div className="mt-12 flex flex-wrap items-center gap-6">
                            <button
                                type="button"
                                onClick={openInquiryModal}
                                data-testid="hero-contact-button"
                                className="group inline-flex items-center gap-4 bg-[#05291C] text-[#FBFBF9] px-8 py-4 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-[#031710] transition-colors duration-500 cursor-pointer"
                            >
                                Begin a Conversation
                                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
                            </button>
                            <a href="#case" data-testid="hero-secondary-link" className="link-underline text-[#05291C] text-[11px] uppercase tracking-[0.28em] font-medium">
                                See Our Most Recent Outcome
                            </a>
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-5 lg:col-span-5 mt-16 md:mt-0 md:pl-6 lg:pl-12 flex flex-col justify-end reveal">
                        <div className="relative">
                            <div className="aspect-[3/4] w-full overflow-hidden bg-[#F2F2ED]">
                                <img
                                    src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?q=80&w=1600&auto=format&fit=crop"
                                    alt="Boardroom interior"
                                    className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[#FBFBF9] p-5 md:p-6 border border-[#05291C]/10 max-w-[260px]">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[#B5965B] font-semibold">
                                    Latest Engagement
                                </div>
                                <div className="mt-3 font-serif text-3xl md:text-4xl text-[#05291C] leading-none">
                                    $100M
                                </div>
                                <div className="mt-2 font-sans text-xs text-[#4A5A53] leading-relaxed">
                                    Closed sale of a privately-held operating company.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div data-testid="hero-marquee" className="mt-32 md:mt-40 border-y border-[#05291C]/10 overflow-hidden">
                <div className="marquee-track flex whitespace-nowrap py-6 text-[#05291C]/60">
                    {Array.from({ length: 2 }).map((_, idx) => (
                        <div key={idx} className="flex items-center shrink-0">
                            {[
                                "Lower Middle Market",
                                "Owner-Operators",
                                "Family-Held",
                                "Discreet Introductions",
                                "Banker Selection",
                                "$25M — $500M Outcomes",
                                "Confidentiality First",
                            ].map((label) => (
                                <span key={`${idx}-${label}`} className="font-serif italic text-2xl md:text-3xl px-12">
                                    {label}
                                    <span className="inline-block ml-12 text-[#B5965B] not-italic">✦</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const WhatWeDo = () => {
    return (
        <section id="what" data-testid="what-we-do-section" className="relative py-24 md:py-40">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-4 reveal">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="block w-6 h-px bg-[#B5965B]" />
                            <span className="text-[11px] uppercase tracking-[0.32em] text-[#4A5A53] font-medium">
                                01 — The Practice
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#05291C] leading-[0.95] tracking-tight font-light">
                            We don't sell<br />companies.<br /><span className="italic">We choose</span><br />the right banker.
                        </h2>
                    </div>

                    <div className="col-span-12 md:col-span-7 md:col-start-6 mt-8 md:mt-16 reveal">
                        <p className="font-sans text-lg md:text-xl leading-relaxed text-[#05291C] font-light max-w-2xl">
                            The single most consequential decision a founder will make in a sale process is{" "}
                            <span className="font-serif italic text-[#05291C]">who runs it.</span>{" "}
                            Yet most owners meet two or three bankers — friends of friends — and pick the one with the best deck.
                        </p>
                        <p className="mt-8 font-sans text-base leading-relaxed text-[#4A5A53] font-light max-w-2xl">
                            Gold Consulting exists to fix that. We are an independent advisory that sits exclusively on the
                            seller's side of the table — before the table exists. We assess the business, define what a
                            winning outcome looks like, and then quietly match you with the investment banker most likely to
                            deliver it: by sector fluency, buyer relationships, deal size, temperament, and track record on
                            transactions that look like yours.
                        </p>

                        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#05291C]/10">
                            {[
                                { k: "Independent", v: "We earn no fees from bankers. Our loyalty sits with one party." },
                                { k: "Curated", v: "A vetted bench of senior bankers across sectors and deal sizes." },
                                { k: "Confidential", v: "Conversations stay private until you decide otherwise." },
                            ].map((item) => (
                                <div key={item.k} className="bg-[#FBFBF9] p-6" data-testid={`pillar-${item.k.toLowerCase()}`}>
                                    <div className="font-serif text-2xl text-[#05291C] mb-3">{item.k}.</div>
                                    <div className="font-sans text-sm text-[#4A5A53] leading-relaxed font-light">{item.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const TransactionHighlight = () => {
    return (
        <section id="case" data-testid="transaction-section" className="relative grain-overlay bg-[#031710] text-[#FBFBF9] py-28 md:py-44 overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
                <div className="flex items-center gap-4 mb-12 reveal">
                    <span className="block w-10 h-px bg-[#B5965B]" />
                    <span className="text-[11px] uppercase tracking-[0.32em] text-[#B5965B] font-medium">
                        02 — Recent Engagement
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-7 reveal">
                        <div className="flex items-baseline">
                            <span className="font-serif font-light leading-none tracking-tight text-[#FBFBF9] text-[28vw] md:text-[18vw] lg:text-[16rem]">
                                $100
                            </span>
                            <span className="font-serif font-normal italic leading-none text-[#B5965B] text-[20vw] md:text-[12vw] lg:text-[11rem] ml-2 md:ml-4">
                                M
                            </span>
                            <span className="font-serif font-light text-[#B5965B] text-[20vw] md:text-[12vw] lg:text-[11rem] leading-none">.</span>
                        </div>
                        <p className="mt-10 font-serif italic text-2xl md:text-3xl text-[#FBFBF9]/80 max-w-xl leading-snug">
                            One founder. One company.<br />One quiet introduction.
                        </p>
                    </div>

                    <div className="col-span-12 md:col-span-5 md:pl-6 lg:pl-12 mt-12 md:mt-24 reveal">
                        <p className="font-sans text-base md:text-[17px] leading-relaxed text-[#FBFBF9]/85 font-light">
                            A founder approached us preparing to sell a
                            building products business he had spent three
                            decades building. He had been offered
                            representation by three bankers — each
                            credible, none ideal. We mapped the buyer
                            universe, profiled the operators of each potential
                            deal team, and introduced him to a senior banker
                            with deep experience in the sector.
                        </p>
                        <p className="mt-6 font-sans text-base md:text-[17px] leading-relaxed text-[#FBFBF9]/85 font-light">
                            The transaction closed at{" "}
                            <span className="font-serif italic text-[#B5965B]">one hundred million dollars</span>{" "}
                            — a result that exceeded every internal forecast the founder had brought to the first meeting.
                        </p>

                        <div className="mt-12 grid grid-cols-2 gap-px bg-[#FBFBF9]/10">
                            {[
                                { k: "Outcome", v: "$100M Sale" },
                                { k: "Process", v: "Confidential" },
                                { k: "Sector", v: "Building Products" },
                                { k: "Role", v: "Banker Selection" },
                            ].map((item) => (
                                <div key={item.k} className="bg-[#031710] p-5" data-testid={`stat-${item.k.toLowerCase()}`}>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#B5965B] font-semibold">{item.k}</div>
                                    <div className="mt-2 font-serif text-xl text-[#FBFBF9]">{item.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-24 reveal">
                    <div className="brass-line max-w-md" />
                    <p className="mt-6 font-sans text-xs uppercase tracking-[0.28em] text-[#FBFBF9]/50">
                        Details withheld in keeping with client confidentiality.
                    </p>
                </div>
            </div>
        </section>
    );
};

const Process = () => {
    const steps = [
        { n: "I", title: "Listen", body: "A private conversation about the business, the family, the timeline, and what a successful exit truly looks like for you." },
        { n: "II", title: "Assess", body: "We examine the company through the lens of the most likely buyer — strategic, financial, or hybrid — and define the deal profile." },
        { n: "III", title: "Match", body: "We introduce you to one — occasionally two — investment bankers selected for sector, size, and chemistry. Never a list. Never a pitch fest." },
        { n: "IV", title: "Step Back", body: "Our work is done before the engagement letter is signed. We remain available, but the banker runs the deal as it should be." },
    ];
    return (
        <section id="process" data-testid="process-section" className="relative py-24 md:py-40 bg-[#F2F2ED]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className="flex items-center gap-4 mb-16 reveal">
                    <span className="block w-10 h-px bg-[#B5965B]" />
                    <span className="text-[11px] uppercase tracking-[0.32em] text-[#4A5A53] font-medium">03 — How We Work</span>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-5 reveal">
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#05291C] font-light">
                            Four steps.<br /><span className="italic">No pageantry.</span>
                        </h2>
                        <p className="mt-8 font-sans text-[#4A5A53] max-w-md leading-relaxed font-light">
                            We move at the pace of the founder. Most engagements run six to ten weeks before an introduction is made.
                        </p>
                    </div>

                    <div className="col-span-12 md:col-span-7">
                        <div className="divide-y divide-[#05291C]/15 border-t border-b border-[#05291C]/15">
                            {steps.map((s) => (
                                <div key={s.n} data-testid={`process-step-${s.n}`} className="grid grid-cols-12 gap-4 py-8 md:py-10 reveal group">
                                    <div className="col-span-2 md:col-span-1 font-serif italic text-xl md:text-2xl text-[#B5965B]">{s.n}</div>
                                    <div className="col-span-10 md:col-span-3 font-serif text-2xl md:text-3xl text-[#05291C]">{s.title}</div>
                                    <div className="col-span-12 md:col-span-8 font-sans text-[15px] md:text-base text-[#4A5A53] leading-relaxed font-light">{s.body}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ContactCTA = () => {
    return (
        <section id="contact" data-testid="contact-section" className="relative py-28 md:py-44 bg-[#FBFBF9]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className="flex items-center gap-4 mb-10 reveal">
                    <span className="block w-10 h-px bg-[#B5965B]" />
                    <span className="text-[11px] uppercase tracking-[0.32em] text-[#4A5A53] font-medium">04 — Begin</span>
                </div>

                <button
                    type="button"
                    onClick={openInquiryModal}
                    data-testid="cta-headline-button"
                    className="block text-left reveal group w-full"
                >
                    <h2 className="font-serif font-normal leading-[1.0] tracking-tight text-[#05291C] text-[16vw] sm:text-[12vw] md:text-[10vw] lg:text-[8.5rem] cursor-pointer">
                        Let's speak,<br />
                        <span className="italic group-hover:text-[#B5965B] transition-colors duration-700">quietly.</span>
                    </h2>
                </button>

                <div className="mt-16 grid grid-cols-12 gap-6 md:gap-12 reveal">
                    <div className="col-span-12 md:col-span-6">
                        <p className="font-sans text-[#4A5A53] text-base md:text-lg leading-relaxed font-light max-w-xl">
                            All initial conversations are protected by mutual confidentiality. There is no fee to be considered;
                            we engage only when both parties agree there is a mandate worth pursuing.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-8">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#4A5A53] font-medium">
                            Begin a Confidential Inquiry
                        </div>
                        <p className="mt-3 font-serif text-2xl md:text-3xl text-[#05291C] leading-snug">
                            A short form. A private response within one business day.
                        </p>
                        <div className="mt-10">
                            <button
                                type="button"
                                onClick={openInquiryModal}
                                data-testid="footer-contact-button"
                                className="inline-flex items-center gap-4 bg-[#05291C] text-[#FBFBF9] px-8 py-4 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-[#031710] transition-colors duration-500 cursor-pointer"
                            >
                                Contact Us
                                <span>→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Footer = () => {
    return (
        <footer data-testid="site-footer" className="border-t border-[#05291C]/10 py-10 bg-[#FBFBF9]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-baseline gap-2">
                    <span className="font-serif text-xl text-[#05291C] font-medium">Gold</span>
                    <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-[#4A5A53]">Consulting</span>
                </div>
                <div className="font-sans text-xs text-[#4A5A53] tracking-wide">
                    © {new Date().getFullYear()} Gold Consulting. Private M&A Advisory.
                </div>
                <button
                    type="button"
                    onClick={openInquiryModal}
                    data-testid="footer-contact-link"
                    className="font-sans text-xs text-[#05291C] link-underline tracking-wide cursor-pointer"
                >
                    Contact
                </button>
            </div>
        </footer>
    );
};

// ====== Inquiry Modal ======

const initialForm = {
    name: "",
    email: "",
    company: "",
    timeline: "",
    message: "",
};

const InquiryModal = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: null, msg: "" });

    const close = useCallback(() => {
        if (submitting) return;
        setOpen(false);
        // Reset only after the transition
        setTimeout(() => {
            setForm(initialForm);
            setStatus({ type: null, msg: "" });
        }, 300);
    }, [submitting]);

    useEffect(() => {
        const onOpen = () => {
            setOpen(true);
            setStatus({ type: null, msg: "" });
        };
        const onKey = (e) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("gc:open-inquiry", onOpen);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("gc:open-inquiry", onOpen);
            window.removeEventListener("keydown", onKey);
        };
    }, [close]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setStatus({ type: "error", msg: "Please complete name, email, and message." });
            return;
        }
        setSubmitting(true);
        setStatus({ type: null, msg: "" });
        try {
            const res = await axios.post(`${API}/inquiries`, {
                name: form.name.trim(),
                email: form.email.trim(),
                company: form.company.trim(),
                timeline: form.timeline.trim(),
                message: form.message.trim(),
            });
            setStatus({
                type: "success",
                msg: res?.data?.message || "Inquiry received. We will be in touch.",
            });
            setForm(initialForm);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong sending your inquiry. Please try again, or write to us directly.";
            setStatus({ type: "error", msg: detail });
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="inquiry-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:py-12"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-modal-title"
        >
            <div
                className="absolute inset-0 bg-[#031710]/70 backdrop-blur-sm"
                onClick={close}
                data-testid="inquiry-modal-backdrop"
            />
            <div className="relative bg-[#FBFBF9] w-full max-w-2xl max-h-full overflow-y-auto border border-[#05291C]/10 shadow-2xl">
                <div className="flex items-start justify-between px-7 md:px-10 pt-8 pb-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-[#B5965B] font-semibold">
                            Confidential Inquiry
                        </div>
                        <h3
                            id="inquiry-modal-title"
                            className="mt-2 font-serif text-3xl md:text-4xl text-[#05291C] leading-tight"
                        >
                            Begin a conversation.
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        data-testid="inquiry-modal-close"
                        aria-label="Close inquiry form"
                        className="text-[#05291C] hover:text-[#B5965B] transition-colors text-2xl leading-none px-2 -mr-2 cursor-pointer"
                    >
                        ×
                    </button>
                </div>

                <p className="px-7 md:px-10 mt-2 font-sans text-sm text-[#4A5A53] leading-relaxed font-light">
                    All submissions are protected by mutual confidentiality. We typically respond within one business day.
                </p>

                <form onSubmit={handleSubmit} className="px-7 md:px-10 py-7 space-y-5" data-testid="inquiry-form">
                    <Field
                        label="Name"
                        required
                        value={form.name}
                        onChange={handleChange("name")}
                        testid="inquiry-input-name"
                        autoComplete="name"
                    />
                    <Field
                        label="Email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange("email")}
                        testid="inquiry-input-email"
                        autoComplete="email"
                    />
                    <Field
                        label="Company"
                        value={form.company}
                        onChange={handleChange("company")}
                        testid="inquiry-input-company"
                        autoComplete="organization"
                    />
                    <Field
                        label="Timeline"
                        placeholder="e.g. exploring, 6–12 months, ready now"
                        value={form.timeline}
                        onChange={handleChange("timeline")}
                        testid="inquiry-input-timeline"
                    />
                    <FieldTextarea
                        label="Message"
                        required
                        value={form.message}
                        onChange={handleChange("message")}
                        testid="inquiry-input-message"
                        placeholder="A few sentences about the business and what you're considering."
                    />

                    {status.msg && (
                        <div
                            data-testid={status.type === "success" ? "inquiry-success" : "inquiry-error"}
                            className={`text-sm leading-relaxed border-l-2 pl-4 py-2 ${
                                status.type === "success"
                                    ? "border-[#B5965B] text-[#05291C] bg-[#F2F2ED]"
                                    : "border-[#8B2E2E] text-[#5C1F1F] bg-[#F7EEEE]"
                            }`}
                        >
                            {status.msg}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                        <button
                            type="button"
                            onClick={close}
                            disabled={submitting}
                            data-testid="inquiry-cancel"
                            className="font-sans text-xs uppercase tracking-[0.24em] text-[#4A5A53] hover:text-[#05291C] transition-colors disabled:opacity-50 cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="inquiry-submit"
                            className="inline-flex items-center gap-3 bg-[#05291C] text-[#FBFBF9] px-7 py-3.5 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-[#031710] transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {submitting ? "Sending…" : "Send Inquiry"}
                            {!submitting && <span>→</span>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Field = ({ label, type = "text", value, onChange, testid, required, placeholder, autoComplete }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.28em] text-[#4A5A53] font-semibold mb-2">
            {label}
            {required && <span className="text-[#B5965B] ml-1">*</span>}
        </span>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            autoComplete={autoComplete}
            data-testid={testid}
            className="w-full bg-transparent border-0 border-b border-[#05291C]/25 focus:border-[#05291C] focus:outline-none focus:ring-0 px-0 py-2 font-serif text-lg text-[#05291C] placeholder:text-[#4A5A53]/50 placeholder:font-sans placeholder:text-sm placeholder:italic transition-colors"
        />
    </label>
);

const FieldTextarea = ({ label, value, onChange, testid, required, placeholder }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.28em] text-[#4A5A53] font-semibold mb-2">
            {label}
            {required && <span className="text-[#B5965B] ml-1">*</span>}
        </span>
        <textarea
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            data-testid={testid}
            rows={4}
            className="w-full bg-transparent border border-[#05291C]/25 focus:border-[#05291C] focus:outline-none focus:ring-0 px-3 py-3 font-sans text-[15px] text-[#05291C] placeholder:text-[#4A5A53]/50 placeholder:italic transition-colors resize-none leading-relaxed"
        />
    </label>
);

function App() {
    useReveal();
    return (
        <div className="app-root" data-testid="app-root">
            <Header />
            <main>
                <Hero />
                <WhatWeDo />
                <TransactionHighlight />
                <Process />
                <ContactCTA />
            </main>
            <Footer />
            <InquiryModal />
        </div>
    );
}

export default App;
